import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth, useRole } from '@/lib/auth/hooks'
import { getAuthCookie } from '@/lib/auth/session-cookie'
import {
  certificationQueryKeys,
  certificationModulesQueryOptions,
  myCertificationsQueryOptions,
} from '@/lib/api/certification-queries'
import { useI18n } from '@/app/lib/i18n'
import { ImageLoader } from '@/ui/components/ImageLoader'
import { Switch } from '@/ui/components/Switch'
import type { CertificationStatus } from '@/app/lib/api/certifications'
import { CertificationCourseCard } from './CertificationCourseCard'
import * as styles from './CertificationHomePage.css'

const imgHero = '/images/certification/hero.png'
const imgHeroThumb = '/images/certification/hero-thumb.webp'
const imgDownloadBg = '/images/certification/download-bg.png'
const imgTechnicianPhoto = '/images/certification/technician-photo.png'
const imgLionIcon = '/logos/lion_square.svg'
const imgGooglePlay = '/images/certification/google-play.svg'
const imgAppStore = '/images/certification/app-store.png'

/** Same store listings used by smart-web (Lion Technician). */
const IOS_APP_URL = 'https://apps.apple.com/us/app/lion-technician/id6714447355'
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.lionenergy.technician'
const SHOW_INACTIVE_STORAGE_KEY = 'certification.portal.showInactive'

export function CertificationHomePage() {
  const { t, tx } = useI18n()
  const { accessToken } = useAuth()
  const { hasRole } = useRole('super-admin')
  const queryClient = useQueryClient()
  const token = accessToken ?? getAuthCookie('access_token')
  const isSuperAdmin = hasRole()
  const [showInactive, setShowInactive] = React.useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SHOW_INACTIVE_STORAGE_KEY) === '1'
  })
  const includeInactive = isSuperAdmin && showInactive

  const modulesQuery = useQuery({
    ...certificationModulesQueryOptions(token ?? '', { includeInactive }),
    enabled: Boolean(token),
  })
  const myCertsQuery = useQuery({
    ...myCertificationsQueryOptions(token ?? ''),
    enabled: Boolean(token),
  })
  const modules = modulesQuery.data ?? []
  const progressByModuleId = React.useMemo(() => {
    const map = new Map<
      string,
      {
        status: CertificationStatus
        progressPercent: number
        passedCount: number
        totalCount: number
      }
    >()
    for (const cert of myCertsQuery.data ?? []) {
      map.set(cert.moduleId, {
        status: cert.status,
        progressPercent: cert.progressPercent,
        passedCount: cert.passedCount ?? 0,
        totalCount: cert.totalCount ?? 0,
      })
    }
    return map
  }, [myCertsQuery.data])
  const loading = modulesQuery.isLoading
  const error = modulesQuery.error
    ? (modulesQuery.error as Error).message || t('certification.home.loadCoursesError')
    : null

  const handleShowInactiveChange = (next: boolean) => {
    setShowInactive(next)
    window.localStorage.setItem(SHOW_INACTIVE_STORAGE_KEY, next ? '1' : '0')
    void queryClient.invalidateQueries({
      queryKey: certificationQueryKeys.modules({ includeInactive: next }),
    })
  }

  return (
    <div className={styles.page}>
      <div className={styles.scrollArea}>

        {/* ── Hero ── */}
        <div className={styles.heroWrap}>
          <div className={styles.hero}>
            <div className={styles.heroBgImage}>
              <ImageLoader src={imgHero} placeholderSrc={imgHeroThumb} />
            </div>
            <div className={styles.heroOverlay} />
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>{tx('certification.home.heroTitle')}</h1>
              <p className={styles.heroSubtitle}>
                {tx('certification.home.heroSubtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* ── Required Courses ── */}
        <section className={styles.coursesSection}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>{tx('certification.home.eyebrow')}</p>
              <h2 className={styles.sectionTitle}>{tx('certification.home.requiredCourses')}</h2>
              {isSuperAdmin && (
                <div className={styles.showInactiveToggle}>
                  <Switch
                    id="certification-show-inactive"
                    label={t('certification.home.showInactive')}
                    checked={showInactive}
                    onCheckedChange={handleShowInactiveChange}
                    showStateText={false}
                  />
                </div>
              )}
            </div>

            {error && <p style={{ color: '#B91C1C', textAlign: 'center' }}>{error}</p>}
            {!error && !loading && modules.length === 0 && (
              <p style={{ textAlign: 'center', color: '#6B7280' }}>
                {tx('certification.home.noCourses')}
              </p>
            )}

            <div className={styles.courseCards}>
              {modules.map((module) => (
                <CertificationCourseCard
                  key={module.id}
                  courseId={module.id}
                  title={module.title}
                  heroImageUrl={module.heroImageUrl}
                  slug={module.slug}
                  isActive={module.isActive}
                  progress={progressByModuleId.get(module.id) ?? null}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── Download Technician App ── */}
        <section className={styles.downloadSection}>
          <div className={styles.downloadBgImage}>
            <ImageLoader src={imgDownloadBg} />
          </div>
          <div className={styles.downloadOverlay} />
          <h2 className={styles.downloadTitle}>{tx('certification.home.downloadAppTitle')}</h2>
          <div className={styles.downloadBadges}>
            <a href={ANDROID_APP_URL} target="_blank" rel="noreferrer" aria-label={t('certification.home.googlePlay')}>
              <img src={imgGooglePlay} alt={t('certification.home.googlePlay')} className={styles.downloadBadge} />
            </a>
            <a href={IOS_APP_URL} target="_blank" rel="noreferrer" aria-label={t('certification.home.appStore')}>
              <img src={imgAppStore} alt={t('certification.home.appStore')} className={styles.downloadBadge} />
            </a>
          </div>
        </section>

        {/* ── Technicians Strive For Excellence ── */}
        <section className={styles.excellenceSection}>
          <div className={styles.excellenceInner}>
            <div className={styles.excellenceText}>
              <img src={imgLionIcon} alt="Lion Energy" className={styles.excellenceLogo} />
              <h2 className={styles.excellenceTitle}>{tx('certification.home.excellenceTitle')}</h2>
              <p className={styles.excellenceBody}>
                {tx('certification.home.excellenceBody')}
              </p>
            </div>
            <div className={styles.excellenceImageWrap}>
              <ImageLoader
                src={imgTechnicianPhoto}
                alt={t('certification.home.technicianPhotoAlt')}
                className={styles.excellenceImage}
              />
            </div>
          </div>
        </section>

      </div>
    </div>
  )
}
